/**
 * <TutorSituationStep> — la première étape de « Gérer une séance » : ce qui arrive au masteur
 * (organism).
 *
 * L'ancien `SelectSituationComponent`, ses réponses et leurs gardes :
 *  - **un cours partagé** (« Décris ta situation ») — le retard, qui ne se signale que le jour même ;
 *    l'absence, qu'une séance d'essai renvoie au support ; « plus disponible » ce créneau, toutes
 *    les semaines, et « Autre », que n'offre plus un remplacement déjà accepté ;
 *  - **un individuel** (« Que souhaites-tu faire ? ») — déplacer cette séance, déplacer le créneau,
 *    ou « Autre ».
 * Toucher une réponse la choisit : c'est l'écran qui décide de l'étape suivante (ou du support).
 */
import React from "react"

import { Stack } from "../../atoms/containers/Stack"
import { OptionCard } from "../../molecules/OptionCard"
import { type UITutorManageSituation } from "../../../ports/primary/ui/types/tutorLives"

export type TutorSituationStepProps = {
    /** Un individuel se déplace ; un cours partagé se signale. */
    isIndividual: boolean
    /** « le mardi à 17h00 » — le créneau régulier (`readableDayTime`). */
    slotLabel: string
    /** Le retard ne se signale que le jour même. */
    canReportDelay: boolean
    /** Une séance d'essai : l'absence passe par le support. Défaut `false`. */
    isTrial?: boolean
    /** Un remplacement est déjà accepté : plus de « plus disponible » ni d'« autre ». Défaut `false`. */
    substitutionAccepted?: boolean
    onSelect: (situation: UITutorManageSituation) => void
    testID?: string
}

export function TutorSituationStep(props: TutorSituationStepProps) {
    const {
        isIndividual,
        slotLabel,
        canReportDelay,
        isTrial = false,
        substitutionAccepted = false,
        onSelect,
        testID,
    } = props

    if (isIndividual) {
        return (
            <Stack gap="2" testID={testID}>
                <OptionCard
                    label="Déplacer cette séance uniquement"
                    selected={false}
                    onPress={() => onSelect("one_occurrence")}
                />
                <OptionCard
                    label={`Déplacer les séances ${slotLabel}`}
                    selected={false}
                    onPress={() => onSelect("all_occurrences")}
                />
                <OptionCard label="Autre" selected={false} onPress={() => onSelect("other")} />
            </Stack>
        )
    }

    return (
        <Stack gap="2" testID={testID}>
            <OptionCard
                label="Je vais être en retard, c'est vraiment inévitable"
                selected={false}
                disabled={!canReportDelay}
                disabledReason="On ne peut signaler un retard que le jour-même"
                onPress={() => onSelect("delay")}
            />
            <OptionCard
                label="Je dois m'absenter"
                selected={false}
                disabled={isTrial}
                disabledReason="Pour les séances d'essai, contacte le support"
                onPress={() => onSelect("absence")}
            />
            {!substitutionAccepted && (
                <>
                    <OptionCard
                        label={`Je ne suis plus disponible ${slotLabel}, toutes les semaines`}
                        selected={false}
                        onPress={() => onSelect("unavailable")}
                    />
                    <OptionCard label="Autre" selected={false} onPress={() => onSelect("other")} />
                </>
            )}
        </Stack>
    )
}
